import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSIconButton from '../../../design-system/DSIconButton';
import KlydoCard from '../../Klydos/KlydosList/components/KlydoCard';
import { PopulatedKlydo } from '../../../api/klydo';
import { ReactComponent as LeftIcon } from '../../../design-system/Icons/LeftIcon.svg';
import { ReactComponent as RightIcon } from '../../../design-system/Icons/RightIcon.svg';

const PAUSE_TIMEOUT_MS = 5000;

type KlydoCarouselProps = {
  klydos: PopulatedKlydo[];
  autoRotateInterval?: number;
};

const KlydoCarousel = ({
  klydos,
  autoRotateInterval = 30000,
}: KlydoCarouselProps) => {
  const { spacing, palette } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!klydos || klydos.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % klydos.length);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [klydos, autoRotateInterval, isPaused]);

  useEffect(() => {
    if (currentIndex >= klydos.length) {
      setCurrentIndex(0);
    }
  }, [klydos, currentIndex]);

  if (!klydos || klydos.length === 0) {
    return null;
  }

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), PAUSE_TIMEOUT_MS);
  };

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? klydos.length - 1 : prevIndex - 1,
    );
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), PAUSE_TIMEOUT_MS);
  }, [klydos.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % klydos.length);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), PAUSE_TIMEOUT_MS);
  }, [klydos.length]);

  const currentKlydo = klydos[currentIndex];

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}
    >
      <DSBox
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {klydos.length > 1 && (
          <DSIconButton
            onClick={handlePrevious}
            sx={{
              padding: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <LeftIcon />
          </DSIconButton>
        )}

        <KlydoCard klydo={currentKlydo} isStatic isMenuHidden />

        {klydos.length > 1 && (
          <DSIconButton
            onClick={handleNext}
            sx={{
              padding: 0,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <RightIcon />
          </DSIconButton>
        )}
      </DSBox>

      {klydos.length > 1 && (
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: spacing(1),
            paddingTop: spacing(2),
            paddingBottom: spacing(1),
          }}
        >
          {klydos.map((_, index) => (
            <DSIconButton
              key={index}
              onClick={() => handleDotClick(index)}
              sx={{
                width: spacing(1),
                height: spacing(1),
                minWidth: spacing(1),
                padding: 0,
                borderRadius: '50%',
                backgroundColor:
                  index === currentIndex
                    ? palette.text.primary
                    : palette.paper.elevation24,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            />
          ))}
        </DSBox>
      )}
    </DSBox>
  );
};

export default KlydoCarousel;
